import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  Work as WorkIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  TrendingUp,
  TrendingDown,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  CloudUpload,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { jobsAPI, resultsAPI } from '../../../services/leadGenAPI';

const JobsAndResults = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [jobDetailsOpen, setJobDetailsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    fetchJobs();
    fetchResults();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await jobsAPI.getJobs();
      setJobs(response.results || response);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async () => {
    try {
      const response = await resultsAPI.getResults();
      setResults(response.results || response);
    } catch (error) {
      console.error('Error fetching results:', error);
      setResults([]);
    }
  };

  const handleJobAction = async (jobId, action) => {
    try {
      let response;
      switch (action) {
        case 'pause':
          // Note: API doesn't have pause for jobs, only campaigns
          setJobs(prev => prev.map(job => 
            job.id === jobId ? { ...job, status: 'paused' } : job
          ));
          break;
        case 'resume':
          // Note: API doesn't have resume for jobs, only campaigns
          setJobs(prev => prev.map(job => 
            job.id === jobId ? { ...job, status: 'running' } : job
          ));
          break;
        case 'stop':
          response = await jobsAPI.cancelJob(jobId);
          setJobs(prev => prev.map(job => 
            job.id === jobId ? { ...job, ...response } : job
          ));
          break;
        case 'retry':
          response = await jobsAPI.retryJob(jobId);
          setJobs(prev => prev.map(job => 
            job.id === jobId ? { ...job, ...response } : job
          ));
          break;
        default:
          throw new Error(`Invalid action: ${action}`);
      }
    } catch (error) {
      console.error('Error performing job action:', error);
      // Refresh the jobs list if action fails
      fetchJobs();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'success';
      case 'completed': return 'info';
      case 'failed': return 'error';
      case 'paused': return 'warning';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running': return <PlayIcon />;
      case 'completed': return <CheckCircleIcon />;
      case 'failed': return <ErrorIcon />;
      case 'paused': return <PauseIcon />;
      case 'pending': return <ScheduleIcon />;
      default: return null;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not started';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const renderJobsTab = () => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" color="text.primary">
          Active Jobs
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => navigate('/lead-gen/suppression/bulk-upload')}
          >
            Bulk Upload
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchJobs}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Jobs Overview */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Active Jobs
              </Typography>
              <Typography variant="h4" component="div">
                {jobs.filter(j => j.status === 'running').length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Currently running
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Completed Today
              </Typography>
              <Typography variant="h4" component="div">
                {jobs.filter(j => j.status === 'completed' && 
                  new Date(j.endTime).toDateString() === new Date().toDateString()).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Successfully finished
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Failed Jobs
              </Typography>
              <Typography variant="h4" component="div" color="error">
                {jobs.filter(j => j.status === 'failed').length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Need attention
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Cost Today
              </Typography>
              <Typography variant="h4" component="div">
                {formatCurrency(jobs.reduce((sum, j) => sum + j.cost, 0))}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Across all jobs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Jobs Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job ID & Campaign</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Lead Source</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No jobs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          #{job.id} - {job.campaign_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {job.job_type} job
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          icon={getStatusIcon(job.status)}
                          label={job.status}
                          color={getStatusColor(job.status)}
                          size="small"
                        />
                        {job.error_message && (
                          <Tooltip title={job.error_message}>
                            <ErrorIcon color="error" fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {job.achieved_count} / {job.target_count}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={job.progress}
                          sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {job.lead_source}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatCurrency(job.cost)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {job.start_time ? (
                          job.end_time ? (
                            `${Math.round((new Date(job.end_time) - new Date(job.start_time)) / (1000 * 60))}m`
                          ) : (
                            `${Math.round((new Date() - new Date(job.start_time)) / (1000 * 60))}m`
                          )
                        ) : (
                          'Not started'
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job.priority}
                        color={getPriorityColor(job.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedJob(job);
                              setJobDetailsOpen(true);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {job.status === 'running' && (
                          <Tooltip title="Pause Job">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleJobAction(job.id, 'pause')}
                            >
                              <PauseIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {job.status === 'paused' && (
                          <Tooltip title="Resume Job">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleJobAction(job.id, 'resume')}
                            >
                              <PlayIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(job.status === 'running' || job.status === 'paused') && (
                          <Tooltip title="Stop Job">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleJobAction(job.id, 'stop')}
                            >
                              <StopIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );

  const renderResultsTab = () => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" color="text.primary">
          Lead Results
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
          >
            Import to CRM
          </Button>
        </Box>
      </Box>

      {/* Results Overview */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Leads
              </Typography>
              <Typography variant="h4" component="div">
                {results.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Generated today
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Generated
              </Typography>
              <Typography variant="h4" component="div" color="success.main">
                {results.filter(r => r.status === 'generated').length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Successfully created
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Suppressed
              </Typography>
              <Typography variant="h4" component="div" color="warning.main">
                {results.filter(r => r.status === 'suppressed').length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Filtered out
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Failed
              </Typography>
              <Typography variant="h4" component="div" color="error">
                {results.filter(r => r.status === 'failed').length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Errors occurred
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Results Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Lead Data</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Quality Score</TableCell>
                <TableCell>Suppression Reason</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>CRM Status</TableCell>
                <TableCell>Generated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No results found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                results.map((result) => (
                  <TableRow key={result.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">{result.name}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {result.email}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {result.company}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={result.status}
                        color={
                          result.status === 'generated' ? 'success' :
                          result.status === 'suppressed' ? 'warning' : 'error'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={result.quality_score}
                          sx={{ width: 50, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="body2">
                          {result.quality_score}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {result.suppression_reason || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatCurrency(result.cost_per_lead)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={result.crm_status.replace('_', ' ')}
                        color={result.crm_status === 'imported' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {formatDate(result.generated_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {result.crm_status !== 'imported' && (
                          <Tooltip title="Import to CRM">
                            <IconButton size="small" color="primary">
                              <UploadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={results.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </>
  );

  const renderJobDetails = () => (
    <Dialog 
      open={jobDetailsOpen} 
      onClose={() => setJobDetailsOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Job Details - {selectedJob?.campaign_name}
      </DialogTitle>
      <DialogContent>
        {selectedJob && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="text.primary">
                      Job Information
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Job ID"
                          secondary={`#${selectedJob.id}`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Campaign"
                          secondary={selectedJob.campaign_name}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Lead Source"
                          secondary={selectedJob.lead_source}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Job Type"
                          secondary={selectedJob.job_type}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Priority"
                          secondary={
                            <Chip
                              label={selectedJob.priority}
                              color={getPriorityColor(selectedJob.priority)}
                              size="small"
                            />
                          }
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="text.primary">
                      Progress & Cost
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Progress"
                          secondary={`${selectedJob.achieved_count} / ${selectedJob.target_count} (${selectedJob.progress}%)`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Status"
                          secondary={
                            <Chip
                              icon={getStatusIcon(selectedJob.status)}
                              label={selectedJob.status}
                              color={getStatusColor(selectedJob.status)}
                              size="small"
                            />
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Total Cost"
                          secondary={formatCurrency(selectedJob.cost)}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Start Time"
                          secondary={formatDate(selectedJob.start_time)}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="End Time"
                          secondary={formatDate(selectedJob.end_time)}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {selectedJob.errorMessage && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <Typography variant="subtitle2" gutterBottom color="text.primary">
                      Error Message
                    </Typography>
                    <Typography variant="body2">
                      {selectedJob.errorMessage}
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setJobDetailsOpen(false)}>
          Close
        </Button>
        <Button variant="contained">
          View Results
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Active Jobs" />
        <Tab label="Job History" />
        <Tab label="Lead Results" />
      </Tabs>

      {activeTab === 0 && renderJobsTab()}
      {activeTab === 1 && (
        <Box>
          <Typography variant="h4" component="h1" mb={3} color="text.primary">
            Job History
          </Typography>
          <Alert severity="info">
            Job history view coming soon. This will show completed and failed jobs with detailed analytics.
          </Alert>
        </Box>
      )}
      {activeTab === 2 && renderResultsTab()}

      {renderJobDetails()}
    </Box>
  );
};

export default JobsAndResults; 